import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { isEmpty, get } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { apiDownloadMessageFile } from 'constants/apiRouting';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import moment from 'moment';
import ReplyAllIcon from '@material-ui/icons/ReplyAll';
import ForwardIcon from '@material-ui/icons/Forward';
import DocIcon from 'icons/doc';
import { AbilityContext } from 'utils/permissions';
import DocAttachmentIcon from 'icons/docAttachment';
import { EMAIL_DATE_FORMAT } from 'constants/config';
import Popover from 'components/Popover/Popover';
import { redirectApp } from 'utils/misc';
import { cleanHTML } from 'utils/dompurify';
import classNames from 'classnames';
import { MESSAGE_BODY_LIMIT } from '../../../constants/config';
import { getFullMessage } from '../../../redux/actions/messageActions';
import Button from '../../buttons/Button';

const styles = theme => ({
  emailContainer: {
    overflowY: 'hidden',
    borderBottom: `1px solid ${theme.palette.secondary['100']}`,
    cursor: 'pointer',
    height: '5rem',
    transition: 'all 0.3s',
    '&$isOpen': {
      overflowY: 'unset',
      height: 'auto',
      cursor: 'auto',
    },
  },
  isOpen: {},
  senderDateContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '0.5rem',
  },
  sender: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.secondary.text,
  },
  senderEmail: {
    color: theme.palette.secondary['900'],
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.secondary['900'],
  },
  receiver: {
    display: 'none',
    marginTop: '0.625rem',
    fontSize: '0.875rem',
    color: theme.palette.secondary['900'],
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      '& $receiverIcon': {
        color: theme.palette.primary.main,
      },
    },
    '&$isOpen': {
      display: 'flex',
    },
  },
  receiverText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    wordBreak: 'normal',
    maxWidth: '30ch',
    display: 'block',
  },
  receiverIcon: {
    color: theme.palette.secondary['300'],
    transition: 'all 0.3s',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&$openedData': {
      transform: 'rotate(180deg)',
      color: theme.palette.primary.main,
    },
  },
  openedData: {},
  emailData: {
    display: 'grid',
    gridTemplateColumns: '3.125rem 16.625rem',
    gridGap: '0.5rem 0.75rem',
    padding: '1rem 1.5rem',
  },
  emailDataLabel: {
    color: theme.palette.gray.contrastText,
    fontSize: '0.75rem',
    fontWeight: 600,
    textAlign: 'right',
  },
  emailDataValue: {
    color: theme.palette.secondary.text,
    fontSize: '0.75rem',
  },
  emailDataValueEmail: {
    color: theme.palette.secondary['900'],
    fontSize: '0.75rem',
  },
  body: {
    marginTop: 0,
    fontSize: '0.875rem',
    color: theme.palette.secondary.text,
    lineHeight: '1.25rem',
    maxWidth: '80%',
    '&$isOpen': {
      maxWidth: '100%',
      marginTop: '2.25rem',
      marginBottom: '2rem',
    },
  },
  attachmentsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '2.25rem',
  },
  attachments: {
    fontSize: '0.875rem',
    color: theme.palette.secondary.text,
    fontWeight: 600,
  },
  downloadAllBtn: {
    height: '2.5rem',
    fontFamily: theme.typography.fontFamily,
    padding: '0.063rem 0.813rem 0.063rem 0.563rem',
    border: `1px solid ${theme.palette.transparent}`,
    borderRadius: '0.375rem',
    color: theme.palette.secondary['900'],
    backgroundColor: theme.palette.white,
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '-0.013rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary['100'],
      border: `1px solid ${theme.palette.primary.main}`,
      '& $btnIcon': {
        fill: theme.palette.primary.main,
      },
    },
  },
  btnIcon: {
    color: theme.palette.secondary['300'],
    marginRight: '0.563rem',
    cursor: 'pointer',
    '&:hover': {
      fill: theme.palette.primary.main,
    },
  },
  historyButton: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1.5rem',
    marginBottom: '2.75rem',
    backgroundColor: theme.palette.transparent,
    border: 'none',
    cursor: 'pointer',
    '&$isOpen': {
      '& $refreshIcon': {
        color: theme.palette.primary.main,
        transform: 'rotateZ(180deg) !important',
      },
      '& $showHistorySpan': {
        color: theme.palette.primary.main,
      },
    },
  },
  refreshIcon: {
    transform: 'rotateZ(0deg) !important',
    color: theme.palette.secondary['300'],
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '0.375rem',
    transition: 'all 1s',
    padding: '0.125rem',
  },
  showHistorySpan: {
    color: theme.palette.secondary['900'],
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  historyContainer: {
    transform: 'scaleY(0)',
    transformOrigin: 'top',
    transition: 'transform 0.26s ease',
    height: 0,
    '&$openedHistory': {
      transform: 'scaleY(1)',
      height: 'auto',
    },
  },
  openedHistory: {},
  attachmentsContainer: {
    display: 'flex',
    marginTop: '1rem',
    marginBottom: '2rem',
  },
  attachment: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.transparent,
    border: `1px solid ${theme.palette.secondary['300']}`,
    cursor: 'pointer',
    borderRadius: '0.375rem',
    width: '9.313rem',
    height: '9.125rem',
    overflow: 'hidden',
    marginRight: '1rem',
  },
  attachmentIcon: {
    marginTop: '-2.5rem',
    width: '3rem',
    height: '3rem',
  },
  attachmentDesc: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2.625rem',
    backgroundColor: theme.palette.secondary['50'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.75rem 1rem',
  },
  docIcon: {
    color: theme.palette.secondary['300'],
    marginTop: '0.25rem',
  },
  attachmentDescLabel: {
    color: theme.palette.secondary['900'],
    fontSize: '0.75rem',
    fontWeight: 600,
    maxWidth: '90%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
  },
  expandButton: {
    height: '1.5rem',
    maxWidth: '3rem !important',
    minWidth: '3rem !important',
  },
});

const Message = (props) => {
  const {
    classes, setMessageEditor, parentMessage, message, isOpen,
  } = props;
  const [isOpenState, setIsOpenState] = React.useState(isOpen);
  const ability = useContext(AbilityContext);
  const [anchorElement, setAnchorEl] = React.useState(null);
  // Flag which shows whether message body collapsed or not.
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  // Flag shows if we have already sent API request to retrieve full message body.
  // To ensure that we are not sending extra API requests.
  const [isFullMessageLoaded, setIsFullMessageLoaded] = React.useState(false);
  // const [showQuotedHistory, setShowQuotedHistory] = React.useState(false);
  // let hasQuotes = false;

  // // Create a temporary element to parse the HTML
  const tempElement = document.createElement('div');
  tempElement.innerHTML = message.body_html;

  // // Find the div with class "gmail_quote" and remove it
  // const gmailQuoteDiv = tempElement.querySelector('.gmail_quote');
  // if (gmailQuoteDiv) {
  //   gmailQuoteDiv.parentNode.removeChild(gmailQuoteDiv);
  //   hasQuotes = true;
  // }

  // // Get the updated email content as a string
  const htmlBodyWithoutQuote = tempElement.innerHTML;

  const handleEmailDataClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmailDataClose = () => {
    setAnchorEl(null);
  };

  // const handleShowHistoryClick = () => {
  //   setShowQuotedHistory(!showQuotedHistory);
  // };

  const handleExpandClick = async () => {
    // Expand or collapse large message body.
    if (!isFullMessageLoaded) {
      props.getFullMessage(message.id);
      setIsCollapsed(false);
      setIsFullMessageLoaded(true);
    } else if (isFullMessageLoaded) {
      setIsCollapsed(!isCollapsed);
    }
  };

  let sentTo =
    message.sent_to &&
    message.sent_to
      .map(to => to.name)
      .filter(name => name !== '')
      .join(', ');
  sentTo =
    sentTo ||
    (message.sent_to && message.sent_to.map(to => to.email).join(', '));
  const canReplyToMessage =
    get(parentMessage, 'account.client_manages_replies', false) ||
    ability.can('reply', 'Message');

  const getMessageBody = () => {
    if (isCollapsed) {
      return message.body_html.substring(0, MESSAGE_BODY_LIMIT);
    }
    // if (showQuotedHistory) {
    //   return message.body_html;
    // }
    return htmlBodyWithoutQuote;
  };

  return (
    <div
      className={classNames(classes.emailContainer, {
        [classes.isOpen]: isOpenState,
      })}
      role="button"
      onClick={() => setIsOpenState(true)}
      onKeyDown={() => {}}
      tabIndex="-1"
    >
      <div className={classes.senderDateContainer}>
        <div className={classes.sender}>
          {message.sent_from_name}{' '}
          <span className={classes.senderEmail}>
            ({message.sent_from_email})
          </span>
        </div>
        <div className={classes.date}>
          {moment(message.received_date)
            .local()
            .format(EMAIL_DATE_FORMAT)}
          {isOpenState && canReplyToMessage && (
            <React.Fragment>
              <ReplyAllIcon
                className={`${classes.btnIcon} ml-3`}
                viewBox="0 0 24 24"
                onClick={() => setMessageEditor({ opened: 'REPLY_ALL', message })}
              />
              <ForwardIcon
                className={classes.btnIcon}
                viewBox="0 0 24 24"
                onClick={() => setMessageEditor({ opened: 'FORWARD', message })}
              />
            </React.Fragment>
          )}
        </div>
      </div>

      <div
        className={classNames(classes.receiver, {
          [classes.isOpen]: isOpenState,
        })}
        onClick={handleEmailDataClick}
        onKeyDown={() => {}}
        role="button"
        tabIndex="-1"
        aria-controls="email-data"
        aria-haspopup="true"
      >
        <div className={classes.receiverText}>to: {sentTo}</div>
        <KeyboardArrowDownRoundedIcon
          className={classNames(classes.receiverIcon, {
            [classes.openedData]: anchorElement,
          })}
        />
      </div>
      <Popover
        id="email-data"
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleEmailDataClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className={classes.emailData}>
          <div className={classes.emailDataLabel}>From:</div>
          <div className={classes.emailDataValue}>
            {message.sent_from_name}{' '}
            <span className={classes.emailDataValueEmail}>
              ({message.sent_from_email})
            </span>
          </div>
          {!isEmpty(message.sent_to) && (
            <React.Fragment>
              <div className={classes.emailDataLabel}>To:</div>
              <div className={classes.emailDataValue}>
                {message.sent_to.map(to => (
                  <div>
                    {to.name}{' '}
                    <span className={classes.emailDataValueEmail}>
                      ({to.email})
                    </span>
                  </div>
                ))}
              </div>
            </React.Fragment>
          )}
          {!isEmpty(message.cc) && (
            <React.Fragment>
              <div className={classes.emailDataLabel}>Cc:</div>
              <div className={classes.emailDataValue}>
                {message.cc.map(cc => (
                  <div>
                    {cc.name}{' '}
                    <span className={classes.emailDataValueEmail}>
                      ({cc.email})
                    </span>
                  </div>
                ))}
              </div>
            </React.Fragment>
          )}
          <div className={classes.emailDataLabel}>Date:</div>
          <div className={classes.emailDataValue}>
            {moment(message.received_date)
              .local()
              .format(EMAIL_DATE_FORMAT)}
          </div>
          <div className={classes.emailDataLabel}>Subject:</div>
          <div className={classes.emailDataValue}>{message.subject}</div>
        </div>
      </Popover>
      <div
        className={classNames(classes.body, { [classes.isOpen]: isOpenState })}
      >
        {message.body_html && isOpenState ? (
          <>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: cleanHTML(getMessageBody()) }}
            />
            {/* {!showQuotedHistory && hasQuotes && (
              <Button
                color="expandButton"
                variant="outlined"
                onClick={handleShowHistoryClick}
                className={`${classes.expandButton} mt-4`}
                loading={message.loadingSubMessage}
              >
                <MoreHorizIcon />
              </Button>
            )} */}
          </>
        ) : (
          <div className="text-truncate">
            {isCollapsed
              ? message.body_plain.substring(0, MESSAGE_BODY_LIMIT)
              : message.body_plain}
          </div>
        )}
        {(message.is_truncated || isFullMessageLoaded) && isOpenState && (
          <Button
            color="expandButton"
            variant="outlined"
            onClick={handleExpandClick}
            className={classes.expandButton}
            loading={message.loadingSubMessage}
          >
            <MoreHorizIcon />
          </Button>
        )}
      </div>
      <div>
        {!isEmpty(message.files) && (
          <React.Fragment>
            <div className={classes.attachmentsSection}>
              <div className={classes.attachments}>
                {message.files.length} Attachment
                {message.files.length > 1 ? 's' : ''}
              </div>
              {/* <button
                className={classes.downloadAllBtn}
                onClick={handleDownloadAllClick}
              >
                <DownloadIcon className={classes.btnIcon} viewBox="0 0 24 24" />
                Download all
              </button> */}
            </div>
            <div className={classes.attachmentsContainer}>
              {message.files.map(file => (
                <button
                  className={classes.attachment}
                  onClick={() =>
                    redirectApp(apiDownloadMessageFile(message.id), {
                      id: file.id,
                      content_type: file.content_type,
                      filename: file.filename,
                    })
                  }
                >
                  <DocAttachmentIcon
                    className={classes.attachmentIcon}
                    viewBox="0 0 32 42"
                  />
                  <div className={classes.attachmentDesc}>
                    <DocIcon className={classes.docIcon} />
                    <div className={classes.attachmentDescLabel}>
                      {file.filename}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

Message.propTypes = {
  classes: PropTypes.element.isRequired,
  parentMessage: PropTypes.shape({}).isRequired,
  message: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool,
  messages: PropTypes.shape({}).isRequired,
  setMessageEditor: PropTypes.func.isRequired,
  getFullMessage: PropTypes.func.isRequired,
};

Message.defaultProps = {
  isOpen: false,
};

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      messages: state.messages,
    }),
    {
      getFullMessage,
    },
  ),
)(Message);
